/**
 * Page-level horizontal swipe between the main sections.
 *
 * Mirrors the calendar/day carousel physics (shared recognizer: deadzone,
 * axis-lock, fling, rising-edge haptic) but operates at the layout level: a
 * horizontal fling pages between the ordered top-level routes (Today →
 * Calendar → Stats). The content tracks the finger and slides out on commit.
 *
 * Coexistence with per-page horizontal gestures: some pages own their own
 * horizontal swipe (the day carousel on "/", the month carousel on
 * "/calendar"). Those surfaces are marked with `data-swipe-own` and the page
 * swipe refuses to start inside them, so the inner gesture always wins and the
 * two never fight over the axis. Swipes that begin on controls, links or
 * editable fields are ignored too, keeping every tap/keyboard affordance intact.
 */
import { createSignal, onCleanup } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { vibrate } from "@/lib/gestures/haptics";
import { PAGE_SWIPE } from "@/lib/gestures/presets";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

/** Ordered top-level sections the page swipe pages through. */
const PAGE_SWIPE_ROUTES = ["/", "/calendar", "/stats"] as const;

interface PageSwipeOptions {
  /** Current pathname (reactive). */
  pathname: () => string;
  /** Navigate to a sibling section. */
  navigate: (to: string) => void;
  /** True while a dialog / command palette is open — suppress the gesture. */
  isBlocked: () => boolean;
  /** Width source for the slide animation (the content surface). */
  getSurfaceRef: () => HTMLElement | undefined;
}

export function usePageSwipeNav(options: PageSwipeOptions) {
  const [dragX, setDragX] = createSignal(0);
  const [transitioning, setTransitioning] = createSignal(false);

  const routes: readonly string[] = PAGE_SWIPE_ROUTES;
  const currentIndex = () => routes.indexOf(options.pathname());
  const surfaceWidth = () => options.getSurfaceRef()?.clientWidth ?? PAGE_SWIPE.fallbackWidthPx;

  /** Target section for a directional page change. delta>0 = next section. */
  const targetRoute = (delta: number): string | null => {
    const index = currentIndex();
    if (index === -1) {
      return null;
    }
    const next = index + delta;
    if (next < 0 || next >= PAGE_SWIPE_ROUTES.length) {
      return null;
    }
    return PAGE_SWIPE_ROUTES[next] ?? null;
  };

  const snapBack = () => {
    setTransitioning(true);
    setDragX(0);
  };

  /** Animate a directional section change. delta>0 = next (content slides left). */
  const animate = (delta: number) => {
    const to = targetRoute(delta);
    if (!to) {
      snapBack();
      return;
    }
    if (prefersReducedMotion()) {
      setDragX(0);
      options.navigate(to);
      return;
    }
    const width = surfaceWidth();
    setTransitioning(true);
    setDragX(delta > 0 ? -width : width); // current section leaves toward travel
    vibrate(PAGE_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      options.navigate(to);
      // Reset instantly; the new route mounts fresh so there is nothing to
      // slide back in.
      setTransitioning(false);
      setDragX(0);
    }, PAGE_SWIPE.transitionMs);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: PAGE_SWIPE.thresholdPx,
    fling: { ...PAGE_SWIPE.fling },
    canStart: ({ target }) => {
      if (options.isBlocked() || currentIndex() === -1) {
        return false;
      }
      // Defer to pages that own their own horizontal swipe, and never hijack a
      // drag that begins on a control, link or editable field.
      return !closestSelector(
        target,
        "[data-swipe-own], textarea, input, select, button, a, [contenteditable]",
      );
    },
    onStart: () => {
      setTransitioning(false);
    },
    onMove: (sample) => {
      setTransitioning(false);
      // Resist dragging past the first / last section.
      const atEdge =
        (sample.dx > 0 && targetRoute(-1) === null) || (sample.dx < 0 && targetRoute(1) === null);
      setDragX(atEdge ? sample.dx * 0.2 : sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        PAGE_SWIPE.commitThresholdPx,
        surfaceWidth() * PAGE_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next section; swipe right (dx>0) → previous.
        animate(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: () => {
      snapBack();
    },
  });

  const handlers = pointerHandlers(recognizer);

  onCleanup(() => {
    recognizer.cancel();
  });

  const transform = () => `translate3d(${dragX()}px, 0, 0)`;
  const transition = () =>
    transitioning() ? `transform ${PAGE_SWIPE.transitionMs}ms ease-out` : "none";

  return { handlers, transform, transition };
}
